import React from 'react';

import {
  ClientTravelConfig,
  ClientInvoiceConfig,
  ClientShortTurnConfig,
  ClientHolidayConfig,
  ClientMealBreakConfig,

  ClientExpenseConfig,
  ClientGearConfig
} from '../../client_configuration';
import type { ClientFormData } from '../utilities/formDataUtils';
import type { ValidationError } from '@/utils/validation/types';
import type { UserConfigurationState } from '@/types/userConfiguration';

interface BillingStepProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur: (field: keyof ClientFormData) => void;
  onClearFieldValidation: (field: keyof ClientFormData) => void;
  isLoading: boolean;
  errors: ValidationError[];
  touchedFields: Set<string>;
  isTravelManuallyEnabled: boolean;
  setIsTravelManuallyEnabled: (enabled: boolean) => void;
  granularCustomized: {
    breaks: boolean;
    travel: boolean;
    invoice: boolean;
    multipliers: boolean;
    shortTurn: boolean;
    holiday: boolean;
    mealBreak: boolean;
    expenses: boolean;
    gear: boolean;
  };
  onRevertSection: (section: 'breaks' | 'travel' | 'invoice' | 'multipliers' | 'shortTurn' | 'holiday' | 'mealBreak' | 'expenses' | 'gear') => void;
  userDefaults?: UserConfigurationState;
}

// eslint-disable-next-line react-refresh/only-export-components
export const billingSubSteps = [
  { label: 'Travel Policies', targetId: 'billing-travel' },
  { label: 'Invoicing', targetId: 'billing-invoicing' },
  { label: 'Expenses', targetId: 'billing-expenses' },
  { label: 'Gear & Fees', targetId: 'billing-gear' },
];

const BillingStepComponent: React.FC<BillingStepProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  onClearFieldValidation,
  isLoading,
  errors,
  touchedFields,
  isTravelManuallyEnabled,
  setIsTravelManuallyEnabled,
  granularCustomized,
  onRevertSection,
  userDefaults,
}) => {


  return (
    <div className="space-y-6">
      <div id="billing-travel">
        <ClientTravelConfig
          formData={formData}
          handleChange={handleChange as (field: keyof ClientFormData, value: unknown) => void}
          handleSwitchChange={handleSwitchChange}
          onFieldBlur={onFieldBlur}
          onClearFieldValidation={onClearFieldValidation}
          isLoading={isLoading}
          errors={errors}
          touchedFields={touchedFields}
          isTravelManuallyEnabled={isTravelManuallyEnabled}
          setIsTravelManuallyEnabled={setIsTravelManuallyEnabled}
          isCustomized={granularCustomized.travel}
          onRevert={() => onRevertSection('travel')}
        />
      </div>
      <div id="billing-invoicing">
        <ClientInvoiceConfig
          formData={formData}
          handleChange={handleChange}
          handleSwitchChange={handleSwitchChange}
          onFieldBlur={onFieldBlur}
          isLoading={isLoading}
          errors={errors}
          touchedFields={touchedFields}
          isCustomized={granularCustomized.invoice}
          onRevert={() => onRevertSection('invoice')}
        />
      </div>
      <div id="billing-expenses">
        <ClientExpenseConfig
          formData={formData}
          handleChange={handleChange as (field: keyof ClientFormData, value: unknown) => void}
          isCustomized={granularCustomized.expenses}
          onRevert={() => onRevertSection('expenses')}
          errors={errors}
          touchedFields={touchedFields}
        />
      </div>
      <div id="billing-gear">
        <ClientGearConfig
          formData={formData}
          handleChange={handleChange as (field: keyof ClientFormData, value: unknown) => void}
          isCustomized={granularCustomized.gear}
          onRevert={() => onRevertSection('gear')}
          errors={errors}
          touchedFields={touchedFields}
        />
      </div>
    </div>
  );
};

export const BillingStep = React.memo(BillingStepComponent);
